import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Alert,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, AccessTime as AccessTimeIcon } from '@mui/icons-material';
import SubStrategyCard from './forms/SubStrategyCard';
import { SYMBOLS, TIMEFRAMES, EXIT_SIGNALS, CONDITION_TYPES, inputSx, selectSx, TV } from '../constants';
import { validateStrategyIdFormat, checkDuplicateStrategyIds } from '../utils/validation';

const getInitialConditionForType = (type, timeframe) => {
  const baseCondition = {
    type: type,
    name: 'entry_condition',
    enabled: true,
  };
  
  switch (type) {
    case 'time_bound':
      return {
        ...baseCondition,
        early_signal: { name: '', timeframe },
        confirmation_signal: { name: '', timeframe },
        min_minutes: 0,
        max_minutes: 14440,
      };
      
    case 'sequence_with_exclusion':
      return {
        ...baseCondition,
        signals: [],
        excluded_signals: [],
        min_minutes: 0,
        max_minutes: 60,
        reset_on_excluded: true,
      };
      
    case 'simultaneous_signals':
      return {
        ...baseCondition,
        required_signals: [],
        time_tolerance_ms: 1000,
      };
      
    case 'is_bullish_when_signal_arrived':
      return {
        ...baseCondition,
        first_signal: { name: '', timeframe },
        second_signal: { name: '', timeframe },
        excluded_signals: [],
        reset_on_excluded: true,
      };
      
    case 'is_bearish_when_signal_arrived':
      return {
        ...baseCondition,
        first_signal: { name: '', timeframe },
        second_signal: { name: '', timeframe },
        excluded_signals: [],
        reset_on_excluded: true,
      };
      
    default:
      return baseCondition;
  }
};

const DEFAULT_FORM_DATA = {
  bulk_strategy_id: '',
  symbol: 'NQ',
  timeframe: '1m',
  description: '',
  exit_signal: '',
  validity_window_minutes: 2880000,
  confirmation_strategies: [],
  required_strategies: [],
  confirmation_time_window_seconds: 5,
  allow_early_confirmation_refresh: true,
  sub_strategies: [],
  active_time_enabled: false,
  active_start_time: '08:00',
  active_end_time: '10:00',
};

const StrategyForm = ({ open, onClose, onSubmit, mode = 'create', submitting = false, initialData = null }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [formErrors, setFormErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [idValidation, setIdValidation] = useState({});

  // CRITICAL FIX: Update form when initialData changes (edit mode)
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      console.log('🔄 Initializing form with edit data:', initialData);
      
      // Deep clone to avoid reference issues
      const editData = JSON.parse(JSON.stringify(initialData));
      
      // Ensure arrays are properly initialized
      editData.sub_strategies = Array.isArray(editData.sub_strategies) ? editData.sub_strategies : [];
      editData.confirmation_strategies = Array.isArray(editData.confirmation_strategies) ? editData.confirmation_strategies : [];
      editData.required_strategies = Array.isArray(editData.required_strategies) ? editData.required_strategies : [];
      
      console.log('✅ Setting form data with sub_strategies:', editData.sub_strategies);
      setFormData(editData);
      
      // Reset validation state for edit mode
      setIdValidation({});
      setFormErrors({});
    } else if (mode === 'create') {
      // Reset to defaults for create mode
      setFormData(DEFAULT_FORM_DATA);
      setIdValidation({});
      setFormErrors({});
    }
  }, [mode, initialData, open]); // Re-run when dialog opens

  const safeFormData = {
    ...formData,
    sub_strategies: Array.isArray(formData.sub_strategies) ? formData.sub_strategies : [],
    confirmation_strategies: Array.isArray(formData.confirmation_strategies) ? formData.confirmation_strategies : [],
    required_strategies: Array.isArray(formData.required_strategies) ? formData.required_strategies : [],
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateSubStrategyId = (idx, value) => {
    const trimmed = value?.trim() || '';

    if (!trimmed) {
      setIdValidation(prev => ({
        ...prev,
        [idx]: { isValid: false, error: 'Strategy ID is required', checked: true }
      }));
      return;
    }

    const formatValidation = validateStrategyIdFormat(trimmed);
    
    if (!formatValidation.isValid) {
      setIdValidation(prev => ({
        ...prev,
        [idx]: { isValid: false, error: formatValidation.error, checked: true }
      }));
      return;
    }

    if (trimmed === safeFormData.bulk_strategy_id?.trim()) {
      setIdValidation(prev => ({
        ...prev,
        [idx]: { isValid: false, error: 'Cannot match bulk strategy ID', checked: true }
      }));
      return;
    }

    const otherSubIds = safeFormData.sub_strategies
      .map((s, i) => i !== idx ? s.strategy_id?.trim() : null)
      .filter(id => id);
    
    if (otherSubIds.includes(trimmed)) {
      setIdValidation(prev => ({
        ...prev,
        [idx]: { isValid: false, error: 'Duplicate with another condition', checked: true }
      }));
      return;
    }

    setIdValidation(prev => ({
      ...prev,
      [idx]: { isValid: true, error: '', checked: true }
    }));
  };

  const onAddSubStrategy = () => {
    const defaultCondition = getInitialConditionForType('time_bound', safeFormData.timeframe);
    
    const newStrategy = {
      strategy_id: '',
      condition: defaultCondition,
    };
    
    const newSubStrategies = [...safeFormData.sub_strategies, newStrategy];
    handleFieldChange('sub_strategies', newSubStrategies);
    
    setIdValidation(prev => ({
      ...prev,
      [newSubStrategies.length - 1]: { isValid: false, error: 'Strategy ID is required', checked: false }
    }));
  };

  const onUpdateSubStrategy = (idx, field, value) => {
    const updated = [...safeFormData.sub_strategies];
    
    if (field === 'condition' && value.type !== updated[idx].condition?.type) {
      const newCondition = getInitialConditionForType(value.type, safeFormData.timeframe);
      if (value.name) newCondition.name = value.name;
      if (value.enabled !== undefined) newCondition.enabled = value.enabled;
      updated[idx] = { ...updated[idx], condition: newCondition };
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    
    handleFieldChange('sub_strategies', updated);

    if (field === 'strategy_id') {
      validateSubStrategyId(idx, value);
    }
  };

  const onRemoveSubStrategy = (idx) => {
    const strategyToRemove = safeFormData.sub_strategies[idx];
    const updated = safeFormData.sub_strategies.filter((_, i) => i !== idx);
    handleFieldChange('sub_strategies', updated);
    
    const newValidation = {};
    Object.keys(idValidation).forEach(key => {
      const keyIdx = parseInt(key);
      if (keyIdx < idx) {
        newValidation[key] = idValidation[key];
      } else if (keyIdx > idx) {
        newValidation[keyIdx - 1] = idValidation[key];
      }
    });
    setIdValidation(newValidation);
    
    if (strategyToRemove?.strategy_id) {
      handleFieldChange('confirmation_strategies', 
        safeFormData.confirmation_strategies.filter(s => s !== strategyToRemove.strategy_id)
      );
      handleFieldChange('required_strategies', 
        safeFormData.required_strategies.filter(s => s !== strategyToRemove.strategy_id)
      );
    }
  };

  const handleAddConfirmationStrategy = (strategyId) => {
    if (!safeFormData.confirmation_strategies.includes(strategyId)) {
      handleFieldChange('confirmation_strategies', [...safeFormData.confirmation_strategies, strategyId]);
    }
  };

  const handleRemoveConfirmationStrategy = (idx) => {
    handleFieldChange('confirmation_strategies', safeFormData.confirmation_strategies.filter((_, i) => i !== idx));
  };

  const handleAddRequiredStrategy = () => {
    const availableStrategies = safeFormData.sub_strategies.filter(
      s => s.strategy_id && !safeFormData.required_strategies.includes(s.strategy_id)
    );
    if (availableStrategies.length > 0) {
      handleFieldChange('required_strategies', [...safeFormData.required_strategies, availableStrategies[0].strategy_id]);
    }
  };

  const removeRequiredStrategy = (idx) => {
    handleFieldChange('required_strategies', safeFormData.required_strategies.filter((_, i) => i !== idx));
  };

  const validateForm = () => {
    const errors = {};

    if (!safeFormData.bulk_strategy_id?.trim()) {
      errors.bulk_strategy_id = 'Bulk strategy ID is required';
    } else {
      const bulkIdValidation = validateStrategyIdFormat(safeFormData.bulk_strategy_id);
      if (!bulkIdValidation.isValid) {
        errors.bulk_strategy_id = bulkIdValidation.error;
      }
    }

    const subIds = safeFormData.sub_strategies.map(s => s.strategy_id?.trim()).filter(id => id);
    
    if (safeFormData.sub_strategies.length === 0) {
      errors.sub_strategies = 'At least one condition is required';
    } else if (subIds.length !== safeFormData.sub_strategies.length) {
      errors.sub_strategies = 'All conditions must have strategy IDs';
    }

    const uniqueIds = new Set(subIds);
    if (uniqueIds.size !== subIds.length) {
      errors.sub_strategies = 'Duplicate strategy IDs found';
    }

    const duplicateCheck = checkDuplicateStrategyIds(safeFormData.bulk_strategy_id, safeFormData.sub_strategies);
    if (duplicateCheck.isDuplicate) {
      errors.sub_strategies = duplicateCheck.message;
    }

    if (safeFormData.confirmation_strategies.length === 0) {
      errors.confirmation = 'At least one confirmation strategy is required';
    }

    const validSubIds = safeFormData.sub_strategies.map(s => s.strategy_id).filter(id => id);
    const invalidConfirmations = safeFormData.confirmation_strategies.filter(id => !validSubIds.includes(id));
    if (invalidConfirmations.length > 0) {
      errors.confirmation = 'Some confirmation strategies do not exist in conditions';
    }

    const invalidRequired = safeFormData.required_strategies.filter(id => !validSubIds.includes(id));
    if (invalidRequired.length > 0) {
      errors.required = 'Some required strategies do not exist in conditions';
    }

    if (!safeFormData.exit_signal?.trim()) {
      errors.exit_signal = 'Exit signal is required';
    }

    if (safeFormData.active_time_enabled) {
      if (!safeFormData.active_start_time) {
        errors.active_start_time = 'Start time is required when time window is enabled';
      }
      if (!safeFormData.active_end_time) {
        errors.active_end_time = 'End time is required when time window is enabled';
      }
      if (safeFormData.active_start_time && safeFormData.active_end_time) {
        const [startHour, startMin] = safeFormData.active_start_time.split(':').map(Number);
        const [endHour, endMin] = safeFormData.active_end_time.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        
        if (startMinutes >= endMinutes) {
          errors.active_time = 'End time must be after start time';
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = {
      bulk_strategy_id: safeFormData.bulk_strategy_id.trim(),
      symbol: safeFormData.symbol,
      timeframe: safeFormData.timeframe,
      description: safeFormData.description,
      exit_signal: safeFormData.exit_signal,
      validity_window_minutes: safeFormData.validity_window_minutes,
      confirmation_strategies: safeFormData.confirmation_strategies.filter(s => s?.trim()),
      required_strategies: safeFormData.required_strategies.filter(s => s?.trim()),
      confirmation_time_window_seconds: safeFormData.confirmation_time_window_seconds,
      allow_early_confirmation_refresh: safeFormData.allow_early_confirmation_refresh,
      sub_strategies: safeFormData.sub_strategies.map(sub => ({
        strategy_id: sub.strategy_id.trim(),
        condition: sub.condition,
      })),
    };

    if (safeFormData.active_time_enabled) {
      payload.active_time_enabled = true;
      payload.active_start_time = safeFormData.active_start_time;
      payload.active_end_time = safeFormData.active_end_time;
    }

    return payload;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    const payload = buildPayload();
    console.log('📤 Submitting payload:', payload);
    onSubmit(payload);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { bgcolor: TV.bg.primary, borderRadius: '4px', border: `1px solid ${TV.border.primary}` }
      }}
    >
      <DialogTitle sx={{ bgcolor: TV.bg.secondary, borderBottom: `1px solid ${TV.border.primary}`, color: TV.text.primary, fontWeight: 700 }}>
        {mode === 'create' ? '+ New Bulk Strategy' : 'Edit Bulk Strategy'}
      </DialogTitle>

      <DialogContent sx={{ bgcolor: TV.bg.primary, color: TV.text.primary }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TV.accent.primary, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
              Basic Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth
                  label="Bulk Strategy ID" 
                  placeholder="e.g., my_nq_long_strategy"
                  value={safeFormData.bulk_strategy_id} 
                  onChange={(e) => handleFieldChange('bulk_strategy_id', e.target.value)}
                  disabled={mode === 'edit'} 
                  error={!!formErrors.bulk_strategy_id}
                  helperText={formErrors.bulk_strategy_id || '3-100 chars, alphanumeric + underscore/hyphen'}
                  size="small" 
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Symbol</InputLabel>
                  <Select 
                    value={safeFormData.symbol} 
                    label="Symbol"
                    onChange={(e) => handleFieldChange('symbol', e.target.value)}
                    sx={selectSx}
                  >
                    {SYMBOLS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Timeframe</InputLabel>
                  <Select 
                    value={safeFormData.timeframe} 
                    label="Timeframe"
                    onChange={(e) => handleFieldChange('timeframe', e.target.value)}
                    sx={selectSx}
                  >
                    {TIMEFRAMES.map(tf => <MenuItem key={tf} value={tf}>{tf}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  fullWidth
                  label="Description" 
                  value={safeFormData.description} 
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  multiline rows={2}
                  size="small" 
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Exit Signal</InputLabel>
                  <Select 
                    value={safeFormData.exit_signal} 
                    label="Exit Signal"
                    onChange={(e) => handleFieldChange('exit_signal', e.target.value)}
                    sx={selectSx}
                    error={!!formErrors.exit_signal}
                  >
                    <MenuItem value="">-- Select --</MenuItem>
                    {EXIT_SIGNALS.map(sig => <MenuItem key={sig} value={sig}>{sig}</MenuItem>)}
                  </Select>
                </FormControl>
                {formErrors.exit_signal && <Typography sx={{ fontSize: '12px', color: TV.accent.danger, mt: 0.5 }}>{formErrors.exit_signal}</Typography>}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth size="small" type="number" 
                  label="Validity Window (minutes)" 
                  value={safeFormData.validity_window_minutes} 
                  onChange={(e) => handleFieldChange('validity_window_minutes', parseInt(e.target.value) || 0)}
                  sx={inputSx} 
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth size="small" type="number" 
                  label="Confirmation Time Window (seconds)" 
                  value={safeFormData.confirmation_time_window_seconds} 
                  onChange={(e) => handleFieldChange('confirmation_time_window_seconds', parseInt(e.target.value) || 0)}
                  sx={inputSx} 
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={safeFormData.allow_early_confirmation_refresh || false}
                      onChange={(e) => handleFieldChange('allow_early_confirmation_refresh', e.target.checked)}
                      sx={{ color: TV.accent.primary, '&.Mui-checked': { color: TV.accent.primary } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '12px', color: TV.text.primary }}>Allow Early Confirmation Refresh</Typography>}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 2, mb: 2, p: 1.5, border: `1px solid ${TV.border.primary}`, borderRadius: '4px', bgcolor: TV.bg.tertiary }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: safeFormData.active_time_enabled ? 1.5 : 0 }}>
              <AccessTimeIcon sx={{ color: TV.accent.primary, fontSize: '18px' }} />
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TV.accent.primary, textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>
                Active Time Restriction
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={safeFormData.active_time_enabled || false}
                    onChange={(e) => handleFieldChange('active_time_enabled', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: TV.accent.primary,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: TV.accent.primary,
                      },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '11px', color: TV.text.secondary }}>{safeFormData.active_time_enabled ? 'Time Restricted' : '24/7 Active'}</Typography>}
              />
            </Stack>
            
            {!safeFormData.active_time_enabled && (
              <Typography sx={{ fontSize: '10px', color: TV.text.secondary, fontStyle: 'italic', mt: 1 }}>
                Strategy will process signals 24/7 without time restrictions
              </Typography>
            )}
            
            {safeFormData.active_time_enabled && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Time"
                    type="time"
                    value={safeFormData.active_start_time}
                    onChange={(e) => handleFieldChange('active_start_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Time"
                    type="time"
                    value={safeFormData.active_end_time}
                    onChange={(e) => handleFieldChange('active_end_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '10px', color: TV.text.secondary, fontStyle: 'italic' }}>
                    Strategy will only process signals between {safeFormData.active_start_time} and {safeFormData.active_end_time}
                  </Typography>
                  {formErrors.active_time && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {formErrors.active_time}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            )}
          </Box>

          <Box sx={{ height: '1px', bgcolor: TV.border.primary }} />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TV.accent.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Confirmation Strategies ({safeFormData.confirmation_strategies.length})
              </Typography>
              <Button 
                size="small" 
                startIcon={<AddIcon sx={{ fontSize: '14px' }} />}
                onClick={() => {
                  const availableStrategies = safeFormData.sub_strategies.filter(
                    s => !safeFormData.confirmation_strategies.includes(s.strategy_id)
                  );
                  if (availableStrategies.length > 0) {
                    handleAddConfirmationStrategy(availableStrategies[0].strategy_id);
                  }
                }}
                sx={{ fontSize: '10px', color: TV.accent.primary, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}
              >
                Add
              </Button>
            </Stack>
            <Stack spacing={1}>
              {safeFormData.confirmation_strategies.map((strategyId, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select 
                      value={strategyId || ''} 
                      onChange={(e) => {
                        const updated = [...safeFormData.confirmation_strategies];
                        updated[idx] = e.target.value;
                        handleFieldChange('confirmation_strategies', updated);
                      }}
                      sx={selectSx}
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      {safeFormData.sub_strategies.map(sub => (
                        <MenuItem key={sub.strategy_id} value={sub.strategy_id}>{sub.strategy_id}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button 
                    size="small" 
                    onClick={() => handleRemoveConfirmationStrategy(idx)}
                    sx={{ color: TV.accent.danger }}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
            {formErrors.confirmation && <Alert severity="error" sx={{ mt: 1 }}>{formErrors.confirmation}</Alert>}
          </Box>

          <Box sx={{ height: '1px', bgcolor: TV.border.primary }} />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TV.accent.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Required Strategies ({(safeFormData.required_strategies || []).filter(s => s?.trim()).length})
              </Typography>
              <Button 
                size="small" 
                startIcon={<AddIcon sx={{ fontSize: '14px' }} />}
                onClick={handleAddRequiredStrategy}
                sx={{ fontSize: '10px', color: TV.accent.primary, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}
              >
                Add
              </Button>
            </Stack>
            <Stack spacing={1}>
              {(safeFormData.required_strategies || []).map((strategyId, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select 
                      value={strategyId || ''} 
                      onChange={(e) => {
                        const updated = [...(safeFormData.required_strategies || [])];
                        updated[idx] = e.target.value;
                        handleFieldChange('required_strategies', updated);
                      }}
                      sx={selectSx}
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      {safeFormData.sub_strategies.map(sub => (
                        <MenuItem key={sub.strategy_id} value={sub.strategy_id}>{sub.strategy_id}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button 
                    size="small" 
                    onClick={() => removeRequiredStrategy(idx)}
                    sx={{ color: TV.accent.danger }}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
            {formErrors.required && <Alert severity="error" sx={{ mt: 1 }}>{formErrors.required}</Alert>}
          </Box>

          <Box sx={{ height: '1px', bgcolor: TV.border.primary }} />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TV.text.secondary, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Conditions ({safeFormData.sub_strategies.length})
              </Typography>
              <Button 
                size="small" 
                startIcon={<AddIcon sx={{ fontSize: '14px' }} />}
                onClick={onAddSubStrategy}
                sx={{ color: TV.accent.primary, fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                New
              </Button>
            </Stack>
            {formErrors.sub_strategies && <Alert severity="error" sx={{ mb: 1.5 }}>{formErrors.sub_strategies}</Alert>}
            <Stack spacing={1}>
              {safeFormData.sub_strategies.map((sub, idx) => (
                <SubStrategyCard 
                  key={idx}
                  sub={sub} 
                  idx={idx} 
                  onUpdate={onUpdateSubStrategy}
                  onRemove={onRemoveSubStrategy}
                  bulkStrategyTimeframe={safeFormData.timeframe}
                  isConfirmation={(safeFormData.confirmation_strategies || []).includes(sub.strategy_id)}
                  isRequired={(safeFormData.required_strategies || []).includes(sub.strategy_id)}
                  idValidation={idValidation}
                  onValidateId={validateSubStrategyId}
                />
              ))}
            </Stack>
          </Box>

          <Box sx={{ pb: 1 }} />

          <Box>
            <Button 
              fullWidth 
              onClick={() => setShowPreview(!showPreview)}
              sx={{ color: TV.accent.primary, fontWeight: 600, textTransform: 'uppercase', justifyContent: 'flex-start', mb: 1, fontSize: '12px' }}
            >
              {showPreview ? '▼' : '▶'} {showPreview ? 'Hide' : 'Show'} JSON Preview
            </Button>
            {showPreview && (
              <Box 
                sx={{ 
                  bgcolor: TV.bg.secondary, 
                  border: `1px solid ${TV.border.primary}`,
                  borderRadius: '4px',
                  p: 2,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontFamily: '"Courier New", monospace',
                  fontSize: '11px',
                  color: TV.accent.primary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: '1.5',
                }}
              >
                {JSON.stringify(buildPayload(), null, 2)}
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ bgcolor: TV.bg.secondary, borderTop: `1px solid ${TV.border.primary}`, p: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: TV.text.secondary, textTransform: 'uppercase', fontWeight: 600, fontSize: '11px' }}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          sx={{ bgcolor: TV.accent.primary, color: '#fff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', px: 3 }}
        >
          {submitting ? 'Saving...' : 'Save Strategy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StrategyForm;
